import { api } from './client';
import type {
  AuthUser,
  ChangeRequest,
  ChatbotConfig,
  ConverseDraft,
  ConverseResult,
  ConverseTurn,
  Feature,
  FeatureConfirmation,
  FeatureImage,
  FeatureRequest,
  FeatureRequestComment,
  FeatureRequestFile,
  IntroPageType,
  MeResponse,
  Page,
  PageQuery,
  Phase,
  PhaseContractTag,
  PortalBranding,
  Project,
  ProjectDocument,
  ProjectDocumentNote,
  ProjectIntroPage,
} from '../types';
import type { FeatureSearchHit } from '../types/acceptance';

/**
 * Lớp gọi API tập trung. Các agent khác import từ đây thay vì gọi axios rải rác.
 * Chữ ký ổn định để dễ mở rộng; backend có thể bổ sung dần.
 */

/**
 * Backend bọc các endpoint danh sách dạng `{ items: [...] }`. Hàm này "bóc"
 * mảng ra, nhưng vẫn chấp nhận trường hợp response là mảng trần (phòng lệch
 * hợp đồng nhẹ) để tránh vỡ UI.
 */
function unwrapItems<T>(data: unknown): T[] {
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  if (Array.isArray(data)) return data as T[];
  return [];
}

/* ------------------------------- Auth ------------------------------------- */

export const authApi = {
  /** Đăng nhập bằng email HOẶC username + mật khẩu (server set cookie HttpOnly). */
  async login(identifier: string, password: string): Promise<AuthUser> {
    const res = await api.post<AuthUser>('/auth/login', { identifier, password });
    return res.data;
  },
  /** Đăng xuất (server xóa cookie). */
  async logout(): Promise<void> {
    await api.post('/auth/logout');
  },
  /** Lấy thông tin phiên hiện tại (user + roles + permissions). */
  async me(): Promise<MeResponse> {
    const res = await api.get<MeResponse>('/auth/me');
    return res.data;
  },
  /** Đánh dấu vĩnh viễn "Không hướng dẫn nữa" cho tài khoản hiện tại. */
  async dismissOnboardingTour(): Promise<MeResponse> {
    const res = await api.post<MeResponse>('/auth/onboarding-tour/dismiss');
    return res.data;
  },
  /**
   * User tự đổi mật khẩu. `currentPassword` bỏ qua (undefined) khi tài khoản
   * chưa từng có mật khẩu (`has_password === false`, vd Google-only) — lúc đó
   * đây là lần đặt mật khẩu đầu tiên.
   */
  async changePassword(currentPassword: string | undefined, newPassword: string): Promise<MeResponse> {
    const res = await api.post<MeResponse>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return res.data;
  },
  /** Cấu hình công khai của nút đăng nhập Google (không cần đăng nhập). */
  googleConfig: async (): Promise<{ enabled: boolean; client_id: string }> => {
    const { data } = await api.get<{ enabled: boolean; client_id: string }>('/auth/google/config');
    return data;
  },
  /** Đổi ID token của Google lấy cookie phiên WEBQN. */
  googleLogin: async (credential: string): Promise<MeResponse> => {
    const { data } = await api.post<MeResponse>('/auth/google', { credential });
    return data;
  },
};

/* ------------------------------ Projects ---------------------------------- */

export const projectApi = {
  /** Backend trả `{ items, total, limit, offset }` (GET /api/projects). */
  async list(query: PageQuery = {}): Promise<Page<Project>> {
    const res = await api.get<Page<Project>>('/projects', { params: query });
    return res.data;
  },
  async get(id: string): Promise<Project> {
    const res = await api.get<Project>(`/projects/${id}`);
    return res.data;
  },
};

/* ------------------------------- Phases ----------------------------------- */

export const phaseApi = {
  /** Backend trả `{ items, total, limit, offset }` (GET /api/projects/:projectId/phases). */
  async listByProject(projectId: string, query: PageQuery = {}): Promise<Page<Phase>> {
    const res = await api.get<Page<Phase>>(`/projects/${projectId}/phases`, { params: query });
    return res.data;
  },
  async get(id: string): Promise<Phase> {
    const res = await api.get<Phase>(`/phases/${id}`);
    return res.data;
  },
};

/* --------------------------- Contract tags --------------------------------- */

export const contractTagApi = {
  /** Backend trả `{ items: PhaseContractTag[] }` (GET /api/phases/:phaseId/contract-tags). */
  async listByPhase(phaseId: string): Promise<PhaseContractTag[]> {
    const res = await api.get<{ items: PhaseContractTag[] } | PhaseContractTag[]>(
      `/phases/${phaseId}/contract-tags`,
    );
    return unwrapItems<PhaseContractTag>(res.data);
  },
};

/* ------------------------------ Features ---------------------------------- */

export const featureApi = {
  /** Backend trả `{ items, total, limit, offset }` (GET /api/phases/:phaseId/features). */
  async listByPhase(phaseId: string, query: PageQuery = {}): Promise<Page<Feature>> {
    const res = await api.get<Page<Feature>>(`/phases/${phaseId}/features`, { params: query });
    return res.data;
  },
  /**
   * Kèm `open_bug_count` (> 0 = tính năng đang "Fixing") và `total_bug_count`
   * (> 0 = tính năng đã từng được báo lỗi, kể cả khi đã xử lý xong).
   */
  async get(id: string): Promise<Feature & { open_bug_count?: number; total_bug_count?: number }> {
    const res = await api.get<Feature & { open_bug_count?: number; total_bug_count?: number }>(
      `/features/${id}`,
    );
    return res.data;
  },
  /** Backend trả `{ items: FeatureImage[] }` (GET /api/features/:id/images). */
  async listImages(featureId: string): Promise<FeatureImage[]> {
    const res = await api.get<{ items: FeatureImage[] } | FeatureImage[]>(`/features/${featureId}/images`);
    return unwrapItems<FeatureImage>(res.data);
  },
  /**
   * Lịch sử nghiệm thu của feature. Backend không có route
   * `/features/:id/confirmations` — endpoint thực là
   * `GET /api/features/:id/acceptance`, trả về
   * `{ feature_id, current_status, current, history }`; ta lấy `history`
   * (mới nhất trước) để giữ nguyên kiểu trả về FeatureConfirmation[].
   */
  async confirmations(featureId: string): Promise<FeatureConfirmation[]> {
    const res = await api.get<{ history?: FeatureConfirmation[] }>(`/features/${featureId}/acceptance`);
    return Array.isArray(res.data?.history) ? res.data.history : [];
  },
  /** Backend trả `{ items, total, limit, offset }` (GET /api/projects/:id/features/search). */
  async searchInProject(
    projectId: string,
    q: string,
    query: PageQuery = {},
  ): Promise<Page<FeatureSearchHit>> {
    const res = await api.get<Page<FeatureSearchHit>>(`/projects/${projectId}/features/search`, {
      params: { ...query, q },
    });
    return res.data;
  },
  /** Danh sách id nhãn hợp đồng đang gắn cho feature (GET /api/features/:id/contract-tags). */
  async getContractTags(featureId: string): Promise<string[]> {
    const res = await api.get<{ tag_ids: string[] }>(`/features/${featureId}/contract-tags`);
    return res.data.tag_ids ?? [];
  },
  /** Ghi đè toàn bộ tập nhãn hợp đồng của feature (PUT /api/features/:id/contract-tags). */
  async setContractTags(featureId: string, tagIds: string[]): Promise<string[]> {
    const res = await api.put<{ tag_ids: string[] }>(`/features/${featureId}/contract-tags`, {
      tag_ids: tagIds,
    });
    return res.data.tag_ids ?? [];
  },
  /**
   * Admin đánh dấu nhiều tính năng "Hoàn thành" cùng lúc (permission
   * FEATURE_CONFIRM_BULK). `skipped` là id gửi lên nhưng không thuộc dự án
   * này hoặc đã bị xoá — vẫn coi là thành công nếu có ít nhất 1 id hợp lệ.
   */
  async bulkComplete(
    projectId: string,
    featureIds: string[],
  ): Promise<{ updated: string[]; skipped: string[] }> {
    const res = await api.post<{ updated: string[]; skipped: string[] }>(
      `/projects/${projectId}/features/bulk-complete`,
      { featureIds },
    );
    return res.data;
  },
};

/* --------------------------- Change requests ------------------------------ */

export const changeRequestApi = {
  /** Backend trả `{ items, total, limit, offset }` (GET /api/change-requests). */
  async list(query: PageQuery = {}): Promise<Page<ChangeRequest>> {
    const res = await api.get<Page<ChangeRequest>>('/change-requests', { params: query });
    return res.data;
  },
};

/* ------------------------- Yêu cầu chức năng -------------------------------- */

export interface FeatureRequestListParams extends PageQuery {
  project_id?: string;
  status?: string;
}

export interface FeatureRequestUpsertBody {
  title?: string;
  description?: string;
  project_id?: string | null;
  phase_id?: string | null;
}

export interface FeatureRequestConvertBody {
  project_id: string;
  phase_id: string;
  name?: string;
  copy_files?: boolean;
}

export const featureRequestApi = {
  /** Backend trả `{ items, total, limit, offset }` (GET /api/feature-requests). */
  async list(params: FeatureRequestListParams = {}): Promise<Page<FeatureRequest>> {
    const res = await api.get<Page<FeatureRequest>>('/feature-requests', { params });
    return res.data;
  },
  async get(id: string): Promise<FeatureRequest> {
    const res = await api.get<FeatureRequest>(`/feature-requests/${id}`);
    return res.data;
  },
  /** Tạo yêu cầu (permission FEATURE_REQUEST_CREATE). */
  async create(body: FeatureRequestUpsertBody): Promise<FeatureRequest> {
    const res = await api.post<FeatureRequest>('/feature-requests', body);
    return res.data;
  },
  /** Chỉ người tạo, chỉ khi còn NEW. */
  async update(id: string, body: FeatureRequestUpsertBody): Promise<FeatureRequest> {
    const res = await api.put<FeatureRequest>(`/feature-requests/${id}`, body);
    return res.data;
  },
  /** Người tạo khi còn NEW, hoặc permission FEATURE_REQUEST_DELETE. */
  async remove(id: string): Promise<void> {
    await api.delete(`/feature-requests/${id}`);
  },
  /** NEW → REVIEWING (permission FEATURE_REQUEST_CONVERT). */
  async review(id: string): Promise<FeatureRequest> {
    const res = await api.post<FeatureRequest>(`/feature-requests/${id}/review`);
    return res.data;
  },
  /** Chuyển thành tính năng thật — trả về cả yêu cầu đã cập nhật và tính năng mới. */
  async convert(
    id: string,
    body: FeatureRequestConvertBody,
  ): Promise<{ request: FeatureRequest; feature: Feature; copied_file_count: number; skipped_file_count: number }> {
    const res = await api.post<{
      request: FeatureRequest;
      feature: Feature;
      copied_file_count: number;
      skipped_file_count: number;
    }>(`/feature-requests/${id}/convert`, body);
    return res.data;
  },
  /** Từ chối — reason bắt buộc. */
  async reject(id: string, reason: string): Promise<FeatureRequest> {
    const res = await api.post<FeatureRequest>(`/feature-requests/${id}/reject`, { reason });
    return res.data;
  },
  /** Khách hàng thuộc dự án xác nhận yêu cầu — cờ độc lập với status. */
  async confirm(id: string): Promise<FeatureRequest> {
    const res = await api.post<FeatureRequest>(`/feature-requests/${id}/confirm`);
    return res.data;
  },
  /** Gắn/đổi dự án + giai đoạn — người tạo, dùng được ở mọi trạng thái chưa kết thúc. */
  async linkProject(id: string, body: { project_id: string; phase_id?: string | null }): Promise<FeatureRequest> {
    const res = await api.post<FeatureRequest>(`/feature-requests/${id}/link-project`, body);
    return res.data;
  },
};

export const featureRequestCommentsApi = {
  /** Toàn bộ bình luận của một yêu cầu (permission FEATURE_REQUEST_VIEW). */
  async list(requestId: string): Promise<FeatureRequestComment[]> {
    const res = await api.get<{ items: FeatureRequestComment[] }>(`/feature-requests/${requestId}/comments`);
    return res.data.items;
  },
  /**
   * Gửi bình luận rồi upload file (permission FEATURE_REQUEST_COMMENT). Phải
   * tạo bình luận trước để có id gắn file vào — backend nhận 1 tệp mỗi
   * request, giống file bug.
   */
  async create(requestId: string, body: string, files: File[] = []): Promise<FeatureRequestComment> {
    const res = await api.post<FeatureRequestComment>(`/feature-requests/${requestId}/comments`, { body });
    const comment = res.data;
    const uploadedFiles: FeatureRequestFile[] = [];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      const uploaded = await api.post<FeatureRequestFile>(`/feature-request-comments/${comment.id}/files`, form);
      uploadedFiles.push(uploaded.data);
    }
    return { ...comment, files: uploadedFiles };
  },
};

export const featureRequestFileApi = {
  async list(requestId: string): Promise<FeatureRequestFile[]> {
    const res = await api.get<{ items: FeatureRequestFile[] }>(`/feature-requests/${requestId}/files`);
    return res.data.items;
  },
  /** Upload lần lượt từng file — backend nhận multipart/form-data 1 tệp mỗi request. */
  async upload(requestId: string, files: File[]): Promise<FeatureRequestFile[]> {
    const uploaded: FeatureRequestFile[] = [];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<FeatureRequestFile>(`/feature-requests/${requestId}/files`, form);
      uploaded.push(res.data);
    }
    return uploaded;
  },
  async remove(fileId: string): Promise<void> {
    await api.delete(`/feature-request-files/${fileId}`);
  },
};

/* ------------------------------- Portal (thương hiệu) ---------------------- */

export const portalApi = {
  /** Thương hiệu cổng truy cập hiện tại theo domain — null nếu không có cổng. */
  async getPublicBranding(): Promise<PortalBranding | null> {
    const res = await api.get<{ portal: PortalBranding | null }>('/portal');
    return res.data.portal;
  },
};

/* ------------------------- Giới thiệu & hướng dẫn theo dự án ---------------- */

export const projectIntroApi = {
  /** Public — trả null nếu 404 (chưa có nội dung/chưa publish). */
  async getPublic(projectId: string, type: IntroPageType): Promise<{ title: string; content_html: string } | null> {
    try {
      const res = await api.get<{ title: string; content_html: string }>(
        `/public/projects/${projectId}/intro/${type}`,
      );
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) return null;
      throw err;
    }
  },
  async getAdmin(projectId: string, type: IntroPageType): Promise<ProjectIntroPage> {
    const res = await api.get<ProjectIntroPage>(`/projects/${projectId}/intro/${type}`);
    return res.data;
  },
  async upsert(
    projectId: string,
    type: IntroPageType,
    payload: { title: string; content_html: string; is_published: boolean },
  ): Promise<ProjectIntroPage> {
    const res = await api.put<ProjectIntroPage>(`/projects/${projectId}/intro/${type}`, payload);
    return res.data;
  },
  async uploadImage(projectId: string, type: IntroPageType, file: File): Promise<{ id: string; url: string }> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post<{ id: string; url: string }>(`/projects/${projectId}/intro/${type}/images`, form);
    return res.data;
  },
};

/* ------------------------------- Chatbot ----------------------------------- */

export const chatbotApi = {
  async getConfig(): Promise<ChatbotConfig> {
    const res = await api.get<ChatbotConfig>('/chatbot/config');
    return res.data;
  },
  async refine(field: 'title' | 'description', rawText: string): Promise<string> {
    const res = await api.post<{ text: string }>('/chatbot/refine', { field, raw_text: rawText });
    return res.data.text;
  },
  async converse(input: { turns: ConverseTurn[]; draft: ConverseDraft; turnsUsed: number }): Promise<ConverseResult> {
    const res = await api.post<ConverseResult>('/chatbot/converse', {
      turns: input.turns,
      draft: input.draft,
      turns_used: input.turnsUsed,
    });
    return res.data;
  },
};

/* --------------------------- Tài liệu dự án -------------------------------- */

export const projectDocumentApi = {
  async list(projectId: string): Promise<ProjectDocument[]> {
    const res = await api.get<{ items: ProjectDocument[] } | ProjectDocument[]>(
      `/projects/${projectId}/documents`,
    );
    return unwrapItems<ProjectDocument>(res.data);
  },
  async upload(
    projectId: string,
    payload: { file: File; title: string; phaseId: string | null },
  ): Promise<ProjectDocument> {
    const form = new FormData();
    form.append('file', payload.file);
    form.append('title', payload.title);
    if (payload.phaseId) form.append('phase_id', payload.phaseId);
    const res = await api.post<ProjectDocument>(`/projects/${projectId}/documents`, form);
    return res.data;
  },
  async update(
    id: string,
    payload: { title?: string; phaseId?: string | null },
  ): Promise<ProjectDocument> {
    const res = await api.put<ProjectDocument>(`/project-documents/${id}`, {
      title: payload.title,
      phase_id: payload.phaseId,
    });
    return res.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/project-documents/${id}`);
  },
  async createLink(
    projectId: string,
    payload: { title: string; phaseId: string | null; externalUrl: string },
  ): Promise<ProjectDocument> {
    const res = await api.post<ProjectDocument>(`/projects/${projectId}/documents/link`, {
      title: payload.title,
      phase_id: payload.phaseId,
      external_url: payload.externalUrl,
    });
    return res.data;
  },
  async enableShare(id: string): Promise<ProjectDocument> {
    const res = await api.post<ProjectDocument>(`/project-documents/${id}/share`);
    return res.data;
  },
  async revokeShare(id: string): Promise<ProjectDocument> {
    const res = await api.delete<ProjectDocument>(`/project-documents/${id}/share`);
    return res.data;
  },
};

/**
 * Lấy bytes tài liệu qua axios (cookie kèm theo, vì route `/raw` vẫn
 * permission-gated — KHÁC ảnh tính năng). Dùng URL.createObjectURL(blob) ở
 * nơi gọi để gắn vào <img>/<iframe> src hoặc tạo link tải xuống tạm.
 */
export async function fetchProjectDocumentBlob(id: string): Promise<Blob> {
  const res = await api.get<Blob>(`/project-documents/${id}/raw`, { responseType: 'blob' });
  return res.data;
}

export const projectDocumentNoteApi = {
  async list(projectId: string): Promise<ProjectDocumentNote[]> {
    const res = await api.get<{ items: ProjectDocumentNote[] } | ProjectDocumentNote[]>(
      `/projects/${projectId}/document-notes`,
    );
    return unwrapItems<ProjectDocumentNote>(res.data);
  },
  /** Chỉ Admin gọi được — backend tự chặn 403 nếu không phải Admin. */
  async upsert(
    projectId: string,
    payload: { phaseId: string | null; note: string },
  ): Promise<ProjectDocumentNote> {
    const res = await api.put<ProjectDocumentNote>(`/projects/${projectId}/document-notes`, {
      phase_id: payload.phaseId,
      note: payload.note,
    });
    return res.data;
  },
};
